/**
 * AiTutorPresentation
 *
 * The ONLY visible thing in the ai-tutor recording view: the lesson slide
 * player (Sanity `html_url_v3`) in an iframe, driven to the current position
 * via the same `JumpToAnim` postMessage protocol the in-class slides use
 * (see frontend `preloadContent.ts` and class `AllContent`).
 *
 * Position is fed in from AiTutorProvider (sourced from LiveKit room metadata).
 * This component only knows how to (a) load the iframe and (b) jump it to a
 * given (step, slide).
 */
import { useCallback, useEffect, useRef } from 'react';
import { useSlidesCdnCookies } from 'hooks/useSlidesCdnCookies';

export interface SlidePosition {
  /** slide_number from room metadata */
  slide: number;
  /** within-slide animation step — CALIBRATION TARGET (plan item F1) */
  step: number;
  /** cumulative clicks; kept for debugging / future absolute mapping */
  totalClicks: number;
}

interface AiTutorPresentationProps {
  presentationUrl: string;
  position: SlidePosition;
  /**
   * Whether this is the deck currently being presented. Inactive decks still
   * mount + load (preloaded so switches are instant) but are NOT driven to the
   * position — `position` only describes the active deck.
   */
  active?: boolean;
}

const AiTutorPresentation = ({ presentationUrl, position, active = true }: AiTutorPresentationProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const readyRef = useRef(false);
  const cdnReady = useSlidesCdnCookies();

  const sendJump = useCallback((step: number, slide: number) => {
    iframeRef.current?.contentWindow?.postMessage(
      {
        type: 'uprio_custom',
        fn: 'JumpToAnim',
        params: [step, slide, true], // [step, slide, skipAnimation]
      },
      '*', // TODO: tighten to the player origin once known
    );
  }, []);

  // Hand our origin to the iframe on load (mirrors class HtmlContent handshake).
  const handleLoad = useCallback(() => {
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'uprio_origin', origin: window.location.origin },
      '*',
    );
  }, []);

  // When the player reports init_completed, push the current position (if active).
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const { type, evt } = event?.data || {};
      if (type === 'uprio_custom' && evt === 'init_completed') {
        readyRef.current = true;
        if (active) sendJump(position.step, position.slide);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [active, position.step, position.slide, sendJump]);

  // Drive the slide whenever the position changes — or when this deck becomes
  // active (e.g. after a deck switch, jump the preloaded iframe to position).
  useEffect(() => {
    if (readyRef.current && active) {
      sendJump(position.step, position.slide);
    }
  }, [active, position.step, position.slide, sendJump]);

  // No deck until the CDN cookies are in: the player fetches every slide from there.
  if (!presentationUrl || !cdnReady) {
    return null;
  }

  const separator = presentationUrl.includes('?') ? '&' : '?';
  // Clean student-facing render; forTM hides player chrome (matches class recording).
  const src = `${presentationUrl}${separator}userType=student&classType=aiTutor&forTM=true`;

  return (
    <iframe
      ref={iframeRef}
      src={src}
      title="AI Tutor presentation"
      allowFullScreen
      onLoad={handleLoad}
      className="w-full h-full border-0"
      style={{ pointerEvents: 'none' }}
    />
  );
};

export default AiTutorPresentation;
